import type { TPlantControlSchedulerRevision } from './schedule-plant-revision.schema.ts';
type Weekday =
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday'
  | 'Sunday';

type ScheduleTimeSlot = {
  id: string;
  startTime: string;
  endTime: string;
  powerSetpoint: number;
  isActive?: boolean;
};

type LowercaseWeekday = Lowercase<Weekday>;

type SlotAddedAction = {
  day: LowercaseWeekday;
  type: 'slot_added';
  slot: ScheduleTimeSlot;
};

type SlotRemovedAction = {
  day: LowercaseWeekday;
  type: 'slot_removed';
  slot: ScheduleTimeSlot;
};

type SlotUpdatedAction = {
  day: LowercaseWeekday;
  type: 'slot_updated';
  slotId: string;
  changes: {
    startTime?: { from: string; to: string };
    endTime?: { from: string; to: string };
    powerSetpoint?: { from: number; to: number };
    isActive?: { from?: boolean; to?: boolean };
  };
};

export type SchedulerSlotAction = SlotAddedAction | SlotRemovedAction | SlotUpdatedAction;
type SchemaAction = NonNullable<TPlantControlSchedulerRevision['actions']>[number];

export function prettifySchedulerActions(
  actions?: SchemaAction[],
  snapshot?: TPlantControlSchedulerRevision['snapshot']
): string[] {
  if (!actions || actions?.length === 0) return [];

  const capitalizeDay = (day: LowercaseWeekday): Weekday =>
    (day.charAt(0).toUpperCase() + day.slice(1)) as Weekday;

  const lines: string[] = [];

  for (const action of actions) {
    if (action.type === 'slot_added') {
      const dayLabel = capitalizeDay(action.day);
      const timeLabel = `${action.slot.startTime}-${action.slot.endTime}`;
      lines.push(
        `${dayLabel}: Added slot ${timeLabel} with power setpoint ${action.slot.powerSetpoint} and active state ${String(action.slot.isActive)}`
      );
      continue;
    }

    if (action.type === 'slot_removed') {
      const dayLabel = capitalizeDay(action.day);
      const timeLabel = `${action.slot.startTime}-${action.slot.endTime}`;
      lines.push(
        `${dayLabel}: Removed slot ${timeLabel} with power setpoint ${action.slot.powerSetpoint} and active state ${String(action.slot.isActive)}`
      );
      continue;
    }

    if (action.type === 'slot_updated') {
      const dayLabel = capitalizeDay(action.day);
      const parts: string[] = [];
      if (action.changes.startTime || action.changes.endTime) {
        const fromStart = action.changes.startTime?.from ?? '';
        const fromEnd = action.changes.endTime?.from ?? '';
        const toStart = action.changes.startTime?.to ?? '';
        const toEnd = action.changes.endTime?.to ?? '';
        parts.push(`slot ${fromStart}-${fromEnd} to ${toStart}-${toEnd}`);
      } else {
        const dayKey = capitalizeDay(action.day);
        const slotsForDay = (
          snapshot?.weeklySchedule as Record<Weekday, ScheduleTimeSlot[]> | undefined
        )?.[dayKey as Weekday];
        const currentSlot = slotsForDay?.find((s) => s.id === action.slotId);
        const timeLabel = currentSlot ? `${currentSlot.startTime}-${currentSlot.endTime}` : '';
        parts.push(`slot ${timeLabel}`);
      }
      if (action.changes.powerSetpoint) {
        parts.push(
          `power setpoint changed from ${action.changes.powerSetpoint.from} to ${action.changes.powerSetpoint.to}`
        );
      }
      if (action.changes.isActive) {
        parts.push(
          `changed active state from ${String(action.changes.isActive.from)} to ${String(
            action.changes.isActive.to
          )}`
        );
      }
      const tail = parts.length > 0 ? ` ${parts.join(', ')}` : '';
      lines.push(`${dayLabel}: Updated${tail}`);
      continue;
    }

    if (action.type === 'name_updated') {
      const from = action.changes.from ?? '';
      const to = action.changes.to ?? '';
      if (from && to) {
        lines.push(`Name changed from "${from}" to "${to}"`);
      }
      if (!from && to) {
        lines.push(`Name changed to "${to}"`);
      }
      continue;
    }

    if (action.type === 'description_updated') {
      const from = action.changes.from ?? '';
      const to = action.changes.to ?? '';
      lines.push(`Description changed from "${from}" to "${to}"`);
      continue;
    }

    if (action.type === 'active_updated') {
      const from = String(action.changes.from);
      const to = String(action.changes.to);
      lines.push(`Changed active state from ${from} to ${to}`);
      continue;
    }
  }

  return lines;
}
